/**
 * Typed wrapper for the citizen social-disbursement endpoints on
 * service-financial-services (port 3110).
 *
 * Endpoints:
 *   - GET  /v1/disbursements/programs                — list active programmes
 *   - GET  /v1/disbursements/me/recipient            — list citizen's recipient rows
 *   - POST /v1/disbursements/recipients/:id/claim    — claim a PENDING; auto-credits wallet
 *
 * RBAC:
 *   - read paths → Disbursement/read-own
 *   - claim      → Disbursement/claim-own
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface DisbursementProgram {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly sponsorAgency: string;
  readonly allowanceAmount: string;
  readonly currency: string;
  readonly frequency: 'ONE_TIME' | 'MONTHLY' | 'QUARTERLY';
  readonly active: boolean;
}

export interface ListProgramsResponse {
  readonly items: readonly DisbursementProgram[];
}

export interface DisbursementRecipientRow {
  readonly id: string;
  readonly programId: string;
  readonly programCode: string;
  readonly programName: string;
  readonly amount: string;
  readonly currency: string;
  readonly status: 'PENDING' | 'PAID' | 'FAILED';
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export interface ListMyRecipientResponse {
  readonly items: readonly DisbursementRecipientRow[];
}

export interface ClaimResponse extends DisbursementRecipientRow {
  readonly walletBalance: string;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function listDisbursementPrograms(
  options: CallOpts = {},
): Promise<ListProgramsResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListProgramsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/disbursements/programs',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listMyRecipientRows(
  options: CallOpts = {},
): Promise<ListMyRecipientResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListMyRecipientResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/disbursements/me/recipient',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function claimDisbursement(
  recipientId: string,
  options: CallOpts = {},
): Promise<ClaimResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ClaimResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/disbursements/recipients/${encodeURIComponent(recipientId)}/claim`,
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}
